import React, { useState, useEffect, PropsWithChildren } from 'react';
import { CustomHeader } from './CustomHeader';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { BackstageTheme } from '@backstage/theme';
import { useLocation } from 'react-router-dom';
import { useMediaQuery } from '@material-ui/core';
import IconButton from '@material-ui/core/IconButton';
import Drawer from '@material-ui/core/Drawer';
import { AdminOnlyComponent } from '../auth';

// Iconos necesarios para el layout
import CloseIcon from '@material-ui/icons/Close';
import MenuOpenIcon from '@material-ui/icons/MenuOpen';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import { DynamicMenu } from './DynamicMenu';

const drawerWidth = 240;
const collapsedDrawerWidth = 72; // Ancho del menú colapsado (solo iconos)

const useStyles = makeStyles((theme: BackstageTheme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
    width: '100%',
    overflow: 'hidden',
  },
  header: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 1300,
    height: 64,
  },
  menuButton: {
    position: 'fixed',
    top: '50%', // Posiciona el botón a la mitad vertical de la pantalla
    transform: 'translateY(-50%)', // Ajuste para centrar verticalmente
    left: 0,
    zIndex: 1300,
    color: '#1c2c50',
    backgroundColor: 'rgba(255, 221, 0, 0.85)', // Amarillo semi-transparente
    borderRadius: '0 50% 50% 0', // Forma circular en el lado derecho
    padding: '6px 8px 6px 4px', // Padding reducido
    minWidth: '36px',
    minHeight: '36px',
    '&:hover': {
      backgroundColor: '#FFDD00',
    },
    [theme.breakpoints.up('md')]: {
      display: 'none',
    },
    transition: theme.transitions.create(['background-color', 'transform'], {
      duration: theme.transitions.duration.shorter,
    }),
    '&:active': {
      transform: 'translateY(-50%) scale(0.95)', // Mantiene el centrado vertical incluso al hacer clic
    },
    boxShadow: '1px 1px 3px rgba(0,0,0,0.15)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  contentWrapper: {
    display: 'flex',
    flex: 1,
    marginTop: 64, // Altura del header
  },
  drawer: {
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    transition: 'width 0.3s ease-in-out',
  },
  drawerOpen: {
    width: drawerWidth,
    transition: 'width 0.3s ease-in-out',
  },
  drawerClose: {
    width: collapsedDrawerWidth,
    overflowX: 'hidden',
    transition: 'width 0.3s ease-in-out',
  },
  mobileDrawer: {
    display: 'none',
    [theme.breakpoints.down('sm')]: {
      display: 'block',
      '& .MuiDrawer-paper': {
        width: '85%',
        maxWidth: 300,
      },
    },
  },
  drawerHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    padding: theme.spacing(0, 1),
    backgroundColor: '#1c2c50', // Color corporativo azul oscuro
    color: '#fff',
    height: 64,
    position: 'sticky',
    top: 0,
    zIndex: 10,
    boxShadow: '0 2px 4px rgba(0,0,0,0.15)',
    '& .MuiIconButton-root': {
      color: '#fff',
      transition: theme.transitions.create(['transform'], {
        duration: theme.transitions.duration.shorter,
      }),
      '&:active': {
        transform: 'scale(0.9)',
      },
    },
  },
  drawerPaper: {
    marginTop: 64, // Para que esté debajo del header
    height: 'calc(100% - 64px)', // Altura total menos el header
    overflowY: 'auto',
    borderRight: `1px solid ${theme.palette.divider}`,
    boxShadow: '2px 0 4px rgba(0, 0, 0, 0.05)',
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    '&::-webkit-scrollbar': {
      width: '0.4em',
    },
    '&::-webkit-scrollbar-track': {
      background: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: theme.palette.grey[300],
      borderRadius: 4,
    },
    [theme.breakpoints.down('sm')]: {
      marginTop: 0,
      height: '100%',
    },
  },
  drawerPaperOpen: {
    width: drawerWidth,
  },
  drawerPaperClose: {
    width: collapsedDrawerWidth,
    overflowX: 'hidden',
  },
  mobilePaper: {
    width: '85%',
    maxWidth: 300,
    boxShadow: '0px 8px 10px -5px rgba(0,0,0,0.2), 0px 16px 24px 2px rgba(0,0,0,0.14), 0px 6px 30px 5px rgba(0,0,0,0.12)',
  },
  content: {
    flexGrow: 1,
    padding: theme.spacing(3),
    backgroundColor: theme.palette.background.default,
    overflow: 'auto',
    minHeight: 'calc(100vh - 64px)', // Altura total menos el header
    width: '100%',
    maxWidth: '100%', // Usar todo el ancho disponible
    overflowX: 'hidden', // Evitar scroll horizontal
    boxSizing: 'border-box',
    transition: 'margin-left 0.3s ease-in-out',
    [theme.breakpoints.down('md')]: {
      padding: theme.spacing(2),
    },
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(1.5),
    },
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(1),
    },
  },
  menuHeader: {
    padding: theme.spacing(2, 1, 1, 2),
    color: theme.palette.text.secondary,
    fontWeight: 500,
  },
  listItem: {
    paddingLeft: theme.spacing(2),
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
      borderLeft: '4px solid #FFDD00',
      paddingLeft: theme.spacing(1.5),
    },
    '&.Mui-selected': {
      backgroundColor: 'transparent',
      borderLeft: '4px solid #FFDD00',
      paddingLeft: theme.spacing(1.5),
      '&:hover': {
        backgroundColor: theme.palette.action.hover,
      },
    },
    transition: theme.transitions.create(['background-color', 'padding', 'width'], {
      duration: theme.transitions.duration.shorter,
    }),
  },
  listItemCollapsed: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    justifyContent: 'center',
    overflow: 'hidden',
    '&:hover': {
      borderLeft: '4px solid #FFDD00',
      paddingLeft: theme.spacing(1.5),
    },
    '&.Mui-selected': {
      backgroundColor: 'transparent',
      borderLeft: '4px solid #FFDD00',
      paddingLeft: theme.spacing(1.5),
    },
  },
  listItemExpanded: {
    width: drawerWidth,
    maxWidth: drawerWidth,
    position: 'absolute',
    zIndex: 1200,
    backgroundColor: theme.palette.background.paper,
    borderRadius: '0 4px 4px 0',
    boxShadow: '4px 4px 8px rgba(0, 0, 0, 0.15)',
    left: 0,
    padding: theme.spacing(1, 2),
    justifyContent: 'flex-start',
    borderLeft: '4px solid #FFDD00',
    display: 'flex',
    alignItems: 'center',
  },
  listItemHoverText: {
    marginLeft: theme.spacing(1),
    opacity: 1,
    display: 'block',
    color: theme.palette.text.primary,
    fontWeight: 'normal',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  listItemContainer: {
    position: 'relative',
    width: '100%',
    display: 'block',
  },
  listItemIcon: {
    minWidth: 36,
  },
  listItemIconCollapsed: {
    minWidth: 0,
    marginRight: theme.spacing(1.5),
    transition: 'margin-right 0.3s ease-in-out',
  },
  listItemText: {
    transition: theme.transitions.create('opacity', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
  listItemTextCollapsed: {
    transition: 'opacity 0.2s ease',
    opacity: 0,
  },
  listItemTooltip: {
    position: 'absolute',
    left: collapsedDrawerWidth,
    top: '50%',
    transform: 'translateY(-50%)',
    backgroundColor: theme.palette.primary.main,
    padding: theme.spacing(1, 2),
    zIndex: 1300,
    boxShadow: '0px 2px 8px rgba(0,0,0,0.2)',
    borderRadius: '0 4px 4px 0',
    whiteSpace: 'nowrap',
    marginLeft: 0,
    fontSize: '0.875rem',
    fontWeight: 500,
    color: '#FFFFFF',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    minHeight: '36px',
    display: 'flex',
    alignItems: 'center',
    borderLeft: '2px solid rgba(255, 255, 255, 0.5)',
  },
  sectionDivider: {
    margin: theme.spacing(1, 0),
  },
  // Estilos para el tooltip de Material UI
  tooltipCustom: {
    backgroundColor: '#ffffff',
    color: theme.palette.text.primary,
    padding: theme.spacing(1, 2),
    fontSize: '0.875rem',
    fontWeight: 500,
    boxShadow: '0px 2px 8px rgba(0,0,0,0.15)',
    borderRadius: '4px',
    borderLeft: `4px solid ${theme.palette.primary.main}`,
    minHeight: '36px',
    display: 'flex',
    alignItems: 'center',
    pointerEvents: 'none',
    marginLeft: '5px !important',
    maxWidth: 'none !important',
    zIndex: 2500
  },
  tooltipArrowCustom: {
    color: '#ffffff',
    '&::before': {
      border: '1px solid rgba(0,0,0,0.05)'
    }
  },
  tooltipPopperCustom: {
    zIndex: 2500
  },
  appTitle: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(0, 2),
    flexGrow: 1,
    '& img': {
      height: 40,
      marginRight: theme.spacing(1),
    },
  },
  // Estilos para el tooltip de Material UI
  tooltip: {
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
    boxShadow: '2px 0 8px rgba(0,0,0,0.15)',
    fontSize: '0.875rem',
    padding: theme.spacing(1, 2),
    borderLeft: `4px solid ${theme.palette.primary.main}`,
    borderRadius: '0 4px 4px 0',
    marginLeft: '0 !important',
  },
  tooltipArrow: {
    color: theme.palette.background.paper,
  },
  tooltipPopper: {
    zIndex: 1500,
  },
}));

export const CustomLayout: React.FC<PropsWithChildren<{}>> = ({ children }): React.ReactElement => {
  const classes = useStyles();
  const theme = useTheme<BackstageTheme>();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [mobileOpen, setMobileOpen] = useState(false);
  // Persistir el estado del menú colapsado en localStorage
  const [isMenuCollapsed, setIsMenuCollapsed] = useState(() => {
    const savedState = localStorage.getItem('menuCollapsedState');
    return savedState ? JSON.parse(savedState) : false;
  });
  const location = useLocation();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };
  
  // Función para colapsar/expandir el menú
  const toggleMenuCollapse = () => {
    const newState = !isMenuCollapsed;
    setIsMenuCollapsed(newState);
    // Guardar el estado en localStorage para persistencia entre sesiones
    localStorage.setItem('menuCollapsedState', JSON.stringify(newState));
  };
  
  // Estado para tooltip hover en cada item
  // Estado para controlar submenús activos en formato móvil
  const [activeSubmenu, setActiveSubmenu] = useState<number | null>(null);

  // Cerrar el drawer al cambiar de ruta en móvil o redimensionar ventana
  useEffect(() => {
    if (isMobile && mobileOpen) {
      setMobileOpen(false);
    }
  }, [children, isMobile]);
  
  // Manejar resize de ventana
  useEffect(() => {
    const handleResize = () => {
      if (!isMobile && mobileOpen) {
        setMobileOpen(false);
      }
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isMobile, mobileOpen]);
  
  // Asegurar que el menú móvil siempre esté expandido cuando se abra
  useEffect(() => {
    if (isMobile && mobileOpen) {
      // En móvil, cuando se abre el menú, debe estar expandido (no colapsado)
      if (isMenuCollapsed) {
        // Solo modificar la vista, no el estado persistente
        setIsMenuCollapsed(false);
      }
    }
  }, [isMobile, mobileOpen]);

  // Tipos para los elementos del menú
  // Contenido del drawer usando el menú dinámico
  const drawerContent = (
    <DynamicMenu isMenuCollapsed={isMenuCollapsed} />
  );

  return (
    <div className={classes.root}>
      <header className={classes.header}>
        <CustomHeader 
          isMenuCollapsed={isMenuCollapsed}
          toggleMenuCollapse={toggleMenuCollapse}
        />
      </header>
      
      {/* Botón para mostrar/ocultar menú en móviles */}
      {isMobile && (
        <IconButton
          color="inherit"
          aria-label="Abrir menú"
          onClick={handleDrawerToggle}
          className={classes.menuButton}
          size="small"
        >
          {mobileOpen ? <ChevronRightIcon /> : <MenuOpenIcon />}
        </IconButton>
      )}
      
      <div className={classes.contentWrapper}>
        {/* Menú para escritorio (permanente) - oculto en móvil */}
        {!isMobile && (
          <Drawer
            className={`${classes.drawer} ${isMenuCollapsed ? classes.drawerClose : classes.drawerOpen}`}
            variant="permanent"
            anchor="left"
            classes={{
              paper: `${classes.drawerPaper} ${isMenuCollapsed ? classes.drawerPaperClose : classes.drawerPaperOpen}`,
            }}
          >
            <div>
              {drawerContent}
            </div>
          </Drawer>
        )}
        
        {/* Menú para móviles (temporal) */}
        <Drawer
          className={classes.mobileDrawer}
          variant="temporary"
          anchor="left"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          classes={{
            paper: classes.mobilePaper,
          }}
          ModalProps={{
            keepMounted: true, // Mejor rendimiento en dispositivos móviles
          }}
        >
          <div className={classes.drawerHeader}>
            <div className={classes.appTitle}>
              <span>IDP Backstage</span>
            </div>
            <IconButton onClick={handleDrawerToggle}>
              <CloseIcon />
            </IconButton>
          </div>
          <div>
            {drawerContent}
          </div>
        </Drawer>
        
        <div className={`${classes.content} no-double-scroll`}>
          {children}
        </div>
      </div>
    </div>
  );
};
